import { useEffect, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';

// Track user activity and update last_activity_at in profiles
// This helps identify inactive users for re-engagement emails
export const useActivityTracker = () => {
  const { user, isAuthenticated } = useAuth();
  const lastUpdateRef = useRef<number>(0);
  const THROTTLE_MS = 5 * 60 * 1000; // Only update every 5 minutes max

  useEffect(() => {
    if (!isAuthenticated || !user) return;

    const updateActivity = async () => {
      const now = Date.now();
      
      // Throttle updates to avoid excessive DB writes
      if (now - lastUpdateRef.current < THROTTLE_MS) {
        return;
      }
      
      lastUpdateRef.current = now;

      try {
        const { error } = await supabase
          .from('profiles')
          .update({ last_activity_at: new Date().toISOString() })
          .eq('user_id', user.id);

        if (error) {
          console.error('Failed to update activity:', error);
        }
      } catch (err) {
        console.error('Activity tracking error:', err);
      }
    };

    // Update on mount (user opened the app)
    updateActivity();

    // Update on visibility change (user came back to the app)
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        updateActivity();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [isAuthenticated, user?.id]);
};
